import React from 'react'
import moment from 'moment'
import { isNil } from 'lodash-es'
import { AuditDetail, NegotiationDetail, NegotiationStatus, UserPreference } from 'api'
import {
  Card,
  StatusStrip,
  CardContent,
  CardHeader,
  Details,
  Status,
  DetailColumn,
  DetailRow,
  KeyInfo,
  Headline,
  DirectionLabel,
  TicketButton,
  Substatus
} from './cardStyles'
import { CountdownTimerComponent, IntervalWrapper } from 'view/components/timers'
import { TextButtonType } from 'view/components/buttons'
import {
  isTerminalStatus,
  getRFQKeyInfo,
  getRFQHeadline,
  getLiveStatusColor,
  isImportantStatus,
  isWithdrawnOrSubject,
  getLiveStatusTextPriced,
  isLastLook,
  getLiveIcon,
  getLiveSubStatusText,
  isAuditDetail,
  isNegotiationHistory
} from 'view/routes/requests/shared/utils'
import { getOwnDirection, getRateFormatOptions, makeFormatDirection } from '../datagrid/modelFormatters'
import { Locale, Translation } from 'locale'
import { iconSet } from 'view/components/icons'

export interface RFQCardProps {
  negotiationDetail: NegotiationDetail | AuditDetail
  userPreference: UserPreference
  offsetTime?: number
  inTicket?: boolean
  hideLogo?: boolean
  respondedStatus?: NegotiationStatus | null
  onClick: () => unknown
}

interface RFQCardState {
  isHover: boolean
}

export class RFQCard extends React.Component<RFQCardProps, RFQCardState> {
  state = { isHover: false }

  render() {
    return <IntervalWrapper render={this.renderContent} />
  }

  private renderContent = () => {
    const { negotiationDetail, userPreference, inTicket, hideLogo, respondedStatus, onClick } = this.props

    const coloredHeader = isImportantStatus(negotiationDetail.negotiation.status)
    const statusColor = getLiveStatusColor(negotiationDetail, true)
    const rate = isAuditDetail(negotiationDetail) ? negotiationDetail.negotiation.rate : negotiationDetail.negotiation.rate.negotiated
    const rateString = isNil(rate) ? '' : ` @ ${Locale.formatNumber(rate, getRateFormatOptions(negotiationDetail.referenceRate))}`
    const direction = getOwnDirection(negotiationDetail.negotiation.direction, negotiationDetail.ownOrder)
    const showTicketButton = !inTicket && this.state.isHover

    return (
      <Card
        data-test-id="RFQCard"
        dead={negotiationDetail.negotiation.status === NegotiationStatus.CANCELLED}
        inTicket={inTicket}
        onMouseEnter={() => this.setHover(true)}
        onMouseLeave={() => this.setHover(false)}>
        {!inTicket && <StatusStrip data-test-id="RFQCard-StatusStrip" statusColor={statusColor} />}
        <CardContent>
          <CardHeader statusColor={coloredHeader ? statusColor : 'initial'}>
            <Status data-test-id={'RFQCard-CardHeader-Status'}>
              {!inTicket && !hideLogo && iconSet.logoIcon}
              <span>
                {respondedStatus ? this.getRespondedStatusText() : getLiveStatusTextPriced(negotiationDetail)}
                {rateString}
              </span>
            </Status>
            {this.renderTimerOrSubstatus()}
          </CardHeader>
          <Details data-test-id={'RFQCard-Details'}>
            <DetailColumn>
              <DetailRow>
                <KeyInfo>{getRFQKeyInfo(negotiationDetail)}</KeyInfo>
                {!showTicketButton && (
                  <span data-test-id="RFQCard-Timestamp">{Locale.formatTime(negotiationDetail.negotiation.createdAt)}</span>
                )}
              </DetailRow>
              <DetailRow>
                <Headline>
                  <DirectionLabel direction={direction}>{makeFormatDirection(userPreference.directionFormat)(direction)}</DirectionLabel>
                  {getRFQHeadline(negotiationDetail, userPreference)}
                </Headline>
              </DetailRow>
            </DetailColumn>
            {showTicketButton && (
              <DetailColumn>
                <TicketButton data-test-id={'RFQCard-DetailColumn-TicketButton-ViewTicket'} type={TextButtonType.Primary} onClick={onClick}>
                  {Locale.translate(Translation.TC_BUTTON_VIEW_TICKET)}
                </TicketButton>
              </DetailColumn>
            )}
          </Details>
        </CardContent>
      </Card>
    )
  }

  private getRespondedStatusText = () => {
    const {
      negotiationDetail: { ownOrder },
      respondedStatus
    } = this.props

    switch (respondedStatus) {
      case NegotiationStatus.WITHDRAWN:
        return Locale.translate(Translation.TC_WITHDRAWN)

      case NegotiationStatus.CANCELLED:
        return Locale.translate(Translation.TC_CANCELLED)

      case NegotiationStatus.ACCEPTED:
        return Locale.translate(Translation.TC_DONE)

      case NegotiationStatus.REJECTED:
        return Locale.translate(Translation.TC_REJECTED)

      case NegotiationStatus.MISSED:
        return Locale.translate(Translation.TC_MISSED)

      case NegotiationStatus.EXPIRED:
        return Locale.translate(Translation.TC_EXPIRED)

      default:
        return ownOrder ? Locale.translate(Translation.TC_PRICED) : Locale.translate(Translation.TC_QUOTED)
    }
  }

  private renderTimerOrSubstatus = () => {
    const { negotiationDetail, inTicket, respondedStatus, offsetTime } = this.props

    if (respondedStatus && isTerminalStatus(respondedStatus)) {
      return null
    }

    const { negotiation, ownOrder } = negotiationDetail

    const showRFQ = !isNegotiationHistory(negotiation) && !isTerminalStatus(negotiation.status) && (ownOrder ? !inTicket : true)
    const rfqEndTime = isNegotiationHistory(negotiation) ? null : negotiation.rfqEndTime
    const showOTW = negotiation.status === NegotiationStatus.QUOTED
    const otwEndTime = isNegotiationHistory(negotiation) ? null : negotiation.otwEndTime || rfqEndTime

    const isSubject = negotiation.status === NegotiationStatus.QUOTED_SUBJECT
    const icon = getLiveIcon(negotiation.status)
    const substatus = getLiveSubStatusText(negotiationDetail)

    if (!isNegotiationHistory(negotiation) && isLastLook(negotiation)) {
      return (
        <Substatus grey={isSubject}>
          <span>
            {isSubject ? `${Locale.translate(Translation.TC_SUBJECT)} - ` : ''}
            {Locale.translate(Translation.TC_LAST_LOOK)}
          </span>
          <span>{iconSet.timerIcon}</span>
        </Substatus>
      )
    } else if (showOTW && otwEndTime) {
      return respondedStatus ? null : (
        <CountdownTimerComponent
          dataTestId="RFQCard-CountdownTimer"
          serverClientTimeOffset={offsetTime || 0}
          expiryTime={moment(otwEndTime)}
          label="OTW"
          noTick={true}
        />
      )
    } else if (showRFQ && rfqEndTime) {
      return (
        <CountdownTimerComponent
          dataTestId="RFQCard-CountdownTimer"
          serverClientTimeOffset={offsetTime || 0}
          expiryTime={moment(rfqEndTime)}
          label={substatus ? `${substatus} - ` : ''}
          noTick={true}
        />
      )
    } else {
      return (
        <Substatus grey={isWithdrawnOrSubject(negotiation.status) || negotiation.status === NegotiationStatus.CANCELLED}>
          <span>{substatus}</span>
          <span>{icon}</span>
        </Substatus>
      )
    }
  }

  private setHover = (isHover: boolean) => {
    this.setState({ isHover })
  }
}
